import { Badge, Heading, TextStyle } from '@superops/ui-design-system';
import React, { useContext, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { onQuerySearch } from '@nile/reports/src/modules/EfficiencyReport/graphql/resolver';
import { useQuery } from '@nile/graphql';
import { CurrencyText, getTranslatedText, Loader } from '@nile/components';
import { cloneDeep, isEmpty } from 'lodash';
import ReportsContext from '@nile/reports/src/modules/home/contexts/Reports';
import EChart from '@nile/reports/src/modules/charts/components/subpages/EChart';
import { getTransformedData } from '@nile/reports/src/modules/charts/utils/chart-transformation';
import { preFormatFilterObject } from '@nile/reports/src/modules/component/helper';
import { getCellValue, getLabelText, getPercentageValue } from '../../../util';
import { monthOptions } from '@nile/banyan/src/Constants/datetime';
import { COLORS } from '../../../../charts/constants';

function CustomChart(props) {
  const { advancedChart, condition } = props;
  const { name, api, chartType, listInfo, config, option } = advancedChart;

  const reportsContext = useContext(ReportsContext);

  const intl = useIntl();
  const [chartOption, setChartOption] = useState({});
  const [data, setData] = useState([]);

  const { operands, reportCondition } = preFormatFilterObject({
    entity: 'HoursWorklogEntry',
    filterObj: cloneDeep(condition),
    removeLabel: true
  });

  const { client, query } = onQuerySearch(api);
  const months = monthOptions(intl);

  const getLabel = (lbl) => {
    switch (api) {
      case 'getMonthlyLabourRevenue':
      case 'getMonthlyRevenue':
        return months?.find((el) => el?.id === Number(lbl))?.label;
      default:
        return lbl;
    }
  };

  const getSeriesData = (dataSet) => {
    if (!listInfo) {
      let seriesDataSet = [];
      let groupData = [];
      groupData = dataSet?.data?.map((el) => ({
        label: getLabel(el?.label)
      }));
      seriesDataSet = dataSet?.data?.[0]?.data?.map((el) => ({
        attribute: el?.label,
        label: getTranslatedText(
          intl,
          `msp.reports.profitablity.attribute.${el?.label}`
        ),
        data: dataSet?.data?.map((dta) => {
          const obj = dta?.data?.find((dt) => dt?.label === el?.label);
          return { value: obj?.value, type: obj?.metricUnitType };
        })
      }));
      return { group: groupData, series: seriesDataSet };
    }

    const { xAxis, yAxis, tooltipData } = option;
    const seriesData = dataSet?.map((dt) => {
      return [getCellValue(dt, xAxis?.key), getCellValue(dt, yAxis?.key)];
    });

    return {
      data: seriesData,
      rawData: dataSet,
      tooltipData: tooltipData,
      label: {
        xname: getTranslatedText(intl, xAxis?.label || 'app.noData.message'),
        yname: getTranslatedText(intl, yAxis?.label || 'app.noData.message')
      }
    };
  };

  const { loading, refetch: refetchData } = useQuery(query, {
    client,
    fetchPolicy: 'cache-and-network',
    variables: {
      input: {
        page: 1,
        pageSize: 20,
        condition: isEmpty(condition)
          ? null
          : (reportCondition &&
              !isEmpty(operands) && {
                joinOperator: 'AND',
                operands: operands || null
              }) ||
            null
      }
    },
    onCompleted(res) {
      const seriesData = getSeriesData(res[api]) || [];
      const chartopt = getTransformedData(seriesData, intl, chartType, {
        customChart: true,
        ...config
      });
      setData(res[api]);
      if (!isEmpty(res[api])) {
        setChartOption(chartopt);
      }
    }
  });

  useEffect(() => {
    refetchData();
  }, [condition]);

  return (
    <>
      <div className="flex mb20">
        <TextStyle weight="SemiBold">
          <FormattedMessage id={name || 'app.noData.message'} />
        </TextStyle>
      </div>
      {loading ? (
        <Loader />
      ) : (
        <>
          <div className="flex h99">
            {config?.showSummaryInfo && (
              <div className="flex w250 fcol g10 mbt20 px10">
                {data?.summaryData?.map((dta, index) => {
                  const lastIndex = data?.summaryData?.length - 1;
                  return (
                    <div className="flex g5">
                      <div
                        className="f-45"
                        style={{
                          color:
                            index !== lastIndex
                              ? COLORS.custom[index]
                              : 'transparent'
                        }}
                      >
                        •
                      </div>
                      <div className="ellipsis">
                        <TextStyle weight="SemiBold">
                          {getTranslatedText(intl, getLabelText(dta?.label))}
                        </TextStyle>
                        <div className="flex actr g10">
                          <Heading element="h1">
                            <CurrencyText
                              value={dta?.value}
                              config={{ notation: 'compact' }}
                            />
                          </Heading>
                          {index !== lastIndex && (
                            <div className="flex">
                              <Badge>
                                {`${getPercentageValue(
                                  dta?.value,
                                  data?.summaryData?.[lastIndex]?.value
                                )} %`}
                              </Badge>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <div className="full-width">
              <EChart
                chartOptions={chartOption}
                chartType={advancedChart?.chartType}
                reportType={reportsContext?.activeReport?.reportType}
                opts={{
                  height: config?.height || '300'
                }}
              />
            </div>
          </div>
        </>
      )}
    </>
  );
}

export default CustomChart;
